import { Component, useState, type ErrorInfo, type ReactNode } from 'react';
// Deliberately the module-level palette, not `usePortalPalette`. This is the
// error boundary: it renders when something above it has already failed, which
// can include the theme provider never mounting.
import { palette } from '../theme';
import { alpha } from '../styleUtils';
import { Box, Button, Collapse, Stack, Typography } from '../primitives';
import * as Icons from '../icons';
import { iconSize } from '@munifocus/tokens';

export interface CrashScreenProps {
  /** Headline — e.g. "Something went wrong". */
  title: string;
  /** Calm, reassuring body copy explaining what to do next. */
  description: string;
  /** Primary action label — typically "Reload". */
  reloadLabel: string;
  onReload: () => void;
  /** Optional secondary action — typically "Back to dashboard". */
  homeLabel?: string;
  onHome?: () => void;
  /** Disclosure toggle label for the technical details block. */
  detailLabel?: string;
  /** Raw error text shown when the details block is expanded (for bug reports). */
  detail?: string;
}

/**
 * Full-screen, on-brand crash page for the web products. Presentational only —
 * all copy is passed in so the boundary that renders it can localize via the
 * app's i18n context. Mirrors the Government Portal design system: cream rest, white card,
 * hairline border, warning-tinted glyph, sentence-case buttons, no emoji.
 */
export function CrashScreen({
  title,
  description,
  reloadLabel,
  onReload,
  homeLabel,
  onHome,
  detailLabel,
  detail,
}: CrashScreenProps) {
  const [showDetail, setShowDetail] = useState(false);

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: palette.background.default,
        px: 2,
        py: 6,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 460,
          backgroundColor: palette.background.paper,
          border: `1px solid ${palette.border.onWhite}`,
          borderRadius: 3,
          boxShadow: `0 1px 2px ${alpha(palette.brand.rail, 0.05)}`,
          p: { xs: 3.5, sm: 4.5 },
          textAlign: 'center',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2.5,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: palette.semantic.warningTint,
          }}
        >
          <Icons.AlertTriangle
            size={iconSize.xl}
            color={palette.semantic.warningInk}
            strokeWidth={2}
          />
        </Box>

        <Typography variant="h2" sx={{ fontSize: 22, color: palette.text.primary, mb: 1.25 }}>
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: 15,
            lineHeight: 1.55,
            color: palette.text.secondary,
            maxWidth: 360,
            mx: 'auto',
          }}
        >
          {description}
        </Typography>

        <Stack spacing={1.25} sx={{ mt: 3.5 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={onReload}
            startIcon={<Icons.RefreshCw size={iconSize.lg} />}
            sx={{ py: 1.3, borderRadius: '28px', fontSize: 15 }}
          >
            {reloadLabel}
          </Button>
          {homeLabel && onHome && (
            <Button
              variant="outlined"
              fullWidth
              onClick={onHome}
              startIcon={<Icons.LayoutDashboard size={iconSize.lg} />}
              sx={{
                py: 1.2,
                borderRadius: '28px',
                fontSize: 15,
                color: palette.text.primary,
                borderColor: palette.border.lineStrong,
                backgroundColor: palette.background.paper,
                '&:hover': {
                  borderColor: palette.brand.accent,
                  backgroundColor: palette.brand.accentTint,
                },
              }}
            >
              {homeLabel}
            </Button>
          )}
        </Stack>

        {detail && detailLabel && (
          <Box sx={{ mt: 2.5, textAlign: 'left' }}>
            <Button
              variant="text"
              onClick={() => setShowDetail((v) => !v)}
              endIcon={
                showDetail ? (
                  <Icons.ChevronUp size={iconSize.md} />
                ) : (
                  <Icons.ChevronDown size={iconSize.md} />
                )
              }
              sx={{
                px: 0,
                minWidth: 0,
                fontSize: 13,
                fontWeight: 600,
                color: palette.text.muted,
                '&:hover': { backgroundColor: 'transparent', color: palette.text.secondary },
              }}
            >
              {detailLabel}
            </Button>
            <Collapse in={showDetail}>
              <Box
                component="pre"
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: palette.background.default,
                  border: `1px solid ${palette.border.line}`,
                  color: palette.text.secondary,
                  fontSize: 12,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowX: 'auto',
                  maxHeight: 200,
                }}
              >
                {detail}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Renders the crash UI. Receives the caught error and a reset callback. */
  fallback: (props: { error: Error; reset: () => void }) => ReactNode;
  /** Side-effect hook for logging/telemetry. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * App-wide React error boundary. Catches render-time crashes anywhere below it
 * and hands control to `fallback`. Keep it inside the i18n + theme providers so
 * the fallback can localize and stay on-brand.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  override render() {
    if (this.state.error) {
      return this.props.fallback({ error: this.state.error, reset: this.reset });
    }
    return this.props.children;
  }
}
